import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Link from '@material-ui/core/Link'
import Box from '@material-ui/core/Box'
import messages from '../messages'
import HandleMarkup from '@src/components/HandleMarkup'
import ErrorIcon from '@material-ui/icons/Error'
import clsx from 'clsx'

const useStyles = makeStyles((theme: Theme) => ({
  validationError: {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(14),
    marginTop: theme.spacing(2),
    '&$configuratorsRedesign': {
      marginTop: theme.spacing(0),
    },
  },
  validationErrorsList: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0),
    paddingLeft: theme.spacing(4),
    '& li > a': {
      color: theme.palette.primary.main,
    },
  },
  ValidationErrorDiv: {
    '& a': {
      color: theme.palette.primary.main,
    },
  },
  errorContainer: {
    display: 'flex',
    paddingTop: theme.spacing(1),
    fontSize: theme.typography.pxToRem(14),
  },
  errorIconContainer: {
    display: 'flex',
    alignItems: 'baseline',
    padding: theme.spacing(1, 1, 0, 0),
  },
  errorIcon: {
    fontSize: theme.typography.pxToRem(12),
  },
  configuratorsRedesign: {
    '& > $validationErrorsList': {
      listStyle: 'none',
      padding: theme.spacing(0),
      fontSize: theme.typography.pxToRem(14),
      '& >li:not(:first-child)': { paddingLeft: theme.spacing(4) },
    },
  },
}))

interface ValidationErrorsProps {
  errors?: string[]
  truncate?: number
  configuratorsRedesign?: boolean
  showIcon?: boolean
  rootClass?: string
}

const ValidationErrors: React.FC<ValidationErrorsProps> = ({
  errors,
  truncate = 5,
  configuratorsRedesign = false,
  showIcon = true,
  rootClass,
}) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  useEffect(() => {
    setOpen(false)
  }, [errors])

  if (!errors || errors.length === 0) {
    return null
  }

  let errorMessage
  if (errors.length === 1) {
    errorMessage = (
      <div className={classes.ValidationErrorDiv}>
        {configuratorsRedesign ? (
          <Box
            className={clsx(classes.errorContainer, {
              [String(rootClass)]: rootClass,
            })}
          >
            {showIcon ? (
              <Box className={classes.errorIconContainer}>
                <ErrorIcon className={classes.errorIcon} />
              </Box>
            ) : null}
            <Box>
              <HandleMarkup value={errors[0]} />
            </Box>
          </Box>
        ) : (
          <HandleMarkup value={errors[0]} />
        )}
      </div>
    )
  } else if (errors.length <= truncate || open) {
    errorMessage = (
      <ul className={classes.validationErrorsList}>
        {errors.map((err, index) => (
          <li key={err}>
            {configuratorsRedesign && index === 0 ? (
              <Box className={classes.errorContainer}>
                <Box className={classes.errorIconContainer}>
                  <ErrorIcon className={classes.errorIcon} />
                </Box>
                <Box>
                  <HandleMarkup value={err} />
                </Box>
              </Box>
            ) : (
              <HandleMarkup value={err} />
            )}
          </li>
        ))}
      </ul>
    )
  } else {
    errorMessage = (
      <>
        <ul className={classes.validationErrorsList}>
          {errors.slice(0, truncate).map((err, index) => (
            <li key={err}>
              {configuratorsRedesign && index === 0 ? (
                <Box className={classes.errorContainer}>
                  <Box className={classes.errorIconContainer}>
                    <ErrorIcon className={classes.errorIcon} />
                  </Box>
                  <Box>
                    <HandleMarkup value={err} />
                  </Box>
                </Box>
              ) : (
                <HandleMarkup value={err} />
              )}
            </li>
          ))}
        </ul>
        <Link component="button" onClick={() => setOpen(true)}>
          <FormattedMessage
            {...messages.INVALID_INPUT_VIEW_MORE}
            values={{
              remaining: errors.length - truncate,
            }}
          />
        </Link>
      </>
    )
  }

  return (
    <div
      className={clsx(classes.validationError, {
        [classes.configuratorsRedesign]: configuratorsRedesign,
      })}
    >
      {errorMessage}
    </div>
  )
}

export default ValidationErrors
